import { Component, Input, Output, EventEmitter, SimpleChanges, ViewChild, ElementRef } from '@angular/core';
import { NgbDropdown } from '@ng-bootstrap/ng-bootstrap';
import * as _ from 'lodash';

@Component({
  selector: 'gf-searchable-dropdown',
  template: `
    <div class="form-control gf-custom-dropdown" ngbDropdown #dropdown="ngbDropdown" (openChange)="onOpenChange($event)" [ngStyle]="{'width': '100%' }">
      <div *ngIf="!isDisabled" class="gf-dropdown-display" ngbDropdownToggle>
          <div>{{selectedDisplay}}</div>
          <span class="glyphicon glyphicon-menu-down gf-dropdown-toggle-icon"></span>
      </div>
      <div *ngIf="isDisabled" class="gf-dropdown-display">
          <div>{{selectedDisplay}}</div>
          <span class="glyphicon glyphicon-menu-down gf-dropdown-toggle-icon"></span>
      </div>
      <div class="gf-dropdown-menu gf-dropdown-scrollable" ngbDropdownMenu>
        <ul class="" style="list-style-type: none; padding-inline-start: 10px !important;">
          <li *ngIf="!(!configurations?.showCheckAll || configurations?.selectionLimit > 0)" style="padding-left: 5px !important;">
            <a (click)="selectAll()">
              <span class="glyphicon glyphicon-ok"></span> 
              {{'Check All' | translate}}
            </a>
          <li *ngIf="configurations?.showUncheckAll">
            <a (click)="deselectAll()">
              <span class="glyphicon glyphicon-remove"></span> 
              {{'Uncheck All' | translate}}
            </a>
          </li>
        </ul>
        <span *ngIf="configurations?.search">
          <input #searchInput
                [(ngModel)]="searchText"
                [name]="fieldId"
                class="form-control gf-dropdown-search-input" 
                placeholder="Search..." 
                (input)="filterItems()" 
                (click)="$event.stopPropagation()" />
          </span>
                <div *ngFor="let group of filteredItems">
                  <div class="gf-dropdown-group-title"
                    *ngIf="showGroupTitle(group)">{{ group.key }}
                  </div>
                  <span *ngFor="let item of group.value">
                    <button class="gf-dropdown-item"
                            [ngStyle]="{'padding-left': (item.depth ? item.depth + 1 : 1) + 'em'}"
                            (click)="selectItem(item)"
                            [class.gf-selected-item]="isSelected(item)">
                        {{item.label}}
                    </button>
                  </span>
              </div>
      </div>
    </div>
  `
})
export class SearchableDropdownComponent {
  @Input() fieldId: string;
  @Input() items: any = [];
  @Input() configurations: any = {
      multiSelect: false,
      search: true,
      displayText: "",
      preventSelect: false,
      showUncheckAll: false,
      showGroups: true // Group titles render whenever items carry a group; pass false to suppress them
  };
  @Input() selected: any | any[] = this.configurations.multiSelect ? [] : {};
  @Input() callback: any;
  @Input() isDisabled: boolean = false;
  @Input() selectedItem: any = null;

  // This emits selection changes and makes them available
  // to the "selected" bound object in the parent object
  @Output() selectedChange = new EventEmitter<any>();

  @ViewChild('dropdown', { static: false }) dropdown!: NgbDropdown;
  @ViewChild('searchInput', { static: false }) searchInput: ElementRef<HTMLInputElement>;

  searchText: string = '';
  filteredItems: any = [];
  selectedDisplay: string = '';
  groupedItems: any = {};
  groupedItemsArray: any[] = [];

  ngOnInit() {
    this.init();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.hasOwnProperty('items')) {
      this.init();
    }
    if (changes.hasOwnProperty('selected')) {
      this.updateSelectedDisplay();
    }
  }

  init() {
    if (_.isEmpty(this.items)) {
      this.items = [];
    };

    if (_.isEmpty(this.configurations.displayText)) {
      this.configurations.displayText = "Select...";
    }

    this.updateSelectedDisplay();
    this.groupItems();

    if (typeof this.configurations.search !== 'boolean') {
      this.configurations.search = (this.items.length >= 10) ? true : false
    }
  }

  groupItems() {
    this.groupedItems = [];
    this.groupedItemsArray = [];

    for (let item of this.items) {
        const group = item.group || 'Ungrouped';
        if (!Array.isArray(this.groupedItems[group])) {
            this.groupedItems[group] = [];
        }
        this.groupedItems[group].push(item);
    }

    // Convert the groupedItems object into an array
    for (let key in this.groupedItems) {
        if (this.groupedItems[key].length > 0) {
            this.groupedItemsArray.push({ key: key, value: this.groupedItems[key] });
        }
    }

    this.filteredItems = this.groupedItemsArray;
  }

  // Put the caret in the filter box as soon as the panel opens so the user can
  // type straight away instead of clicking a second time. Any text left over
  // from a previous open is selected, so typing replaces it.
  onOpenChange(isOpen: boolean) {
    if (!isOpen || !_.get(this.configurations, 'search', false)) {
      return;
    }

    // ng-bootstrap adds the class that reveals the menu after this event, and
    // focus() is a no-op on a hidden element, so defer to the next tick.
    setTimeout(() => {
      const input = this.searchInput?.nativeElement;
      if (input) {
        input.focus();
        input.select();
      }
    });
  }

  selectItem(item: any) {
    if (this.configurations.multiSelect) {
        // Ensure selected is an array.
        if (!Array.isArray(this.selected)) {
            this.selected = [];
        }

        if (!_.isEmpty(_.find(this.selected, {id: item.id}))) {
          _.remove(this.selected, {id: item.id});
          this.updateSelectedDisplay();
          if (typeof this.callback === 'function') {
            this.callback(this.selected);
          } else {
            this.selectedChange.emit(this.selected);
          }
          return;
        }

        // If the selected item is -1, overwrite the array with the new item
        if (_.filter(this.selected, {id: -1}).length > 0) {
          this.selected = [item];
        } else {
          this.selected.push(item);
        }
    } else {
        if (_.get(this.selected, 'id', '') == item.id) {
          this.selected = null;
          if (typeof this.callback === 'function') {
            this.callback(this.selected);
          } else {
            this.selectedChange.emit(this.selected);
          }
          this.updateSelectedDisplay();
          this.dropdown?.close();
          return;
        }

        this.selected = item;
    }

    if (typeof this.callback === 'function') {
      this.callback(this.selected);
    } else {
      this.selectedChange.emit(this.selected);
    }

    this.updateSelectedDisplay();
    if (!this.configurations.multiSelect) {
      this.dropdown?.close();
    }
  }

  selectAll() {
    if (!this.configurations.multiSelect) return;

    // Ensure selected is an array.
    if (!Array.isArray(this.selected)) {
      this.selected = [];
    }

    //Set all available elements as selected
    _.forEach(this.filteredItems, (group) => {
      this.selected = _.union(this.selected, group.value);
    });

    if (typeof this.callback === 'function') {
      this.callback(this.selected);
    } else {
      this.selectedChange.emit(this.selected);
    }

    this.updateSelectedDisplay();
  }

  deselectAll() {
    if (!this.configurations.multiSelect) return;

    //Empty selection
    this.selected = [];

    if (typeof this.callback === 'function') {
      this.callback(this.selected);
    } else {
      this.selectedChange.emit(this.selected);
    }

    this.updateSelectedDisplay();
  }

  updateSelectedDisplay() {
    if (this.configurations.preventSelect) {
      this.selectedDisplay = (this.configurations.displayText) ? this.configurations.displayText : '';
    } else {
      if (this.configurations.multiSelect) {
        this.selectedDisplay = (Array.isArray(this.selected)) ? (this.selected as any[]).map(i => i.label).join(', ') : '';
        if (!this.selectedDisplay?.length) {
          this.selectedDisplay = !_.isEmpty(this.configurations.displayText) ? this.configurations.displayText : "";
        }
      } else {
        this.selectedDisplay = this.selected?.label || (!_.isEmpty(this.configurations.displayText) ? this.configurations.displayText : "");
      }
    }
  }

  filterItems() {
    if (!this.searchText || this.searchText.trim() === '') {
        this.filteredItems = [...this.groupedItemsArray];
        return;
    }

    const searchLowercase = this.searchText.toLowerCase();

    // Filtering the groups and their items
    this.filteredItems = this.groupedItemsArray
        .map(group => {
            // Filter the items in this group
            const filteredItems = group.value && group.value.length 
                ? group.value.filter(item => 
                    item.label && item.label.toLowerCase().includes(searchLowercase)
                  )
                : [];

            // If there are any items left after filtering, return the group with these items
            if (filteredItems.length) {
                return { ...group, value: filteredItems };
            }
            
            // Otherwise, don't return anything for this group
            return null;
        })
        .filter(group => group);  // Remove nulls
  }

  // Group titles render whenever the items carry a group (sprints and milestones
  // arrive pre-tagged by TrackerFieldsService); pass showGroups: false to suppress them.
  showGroupTitle(group: any): boolean {
    return _.get(group, 'key', 'Ungrouped') !== 'Ungrouped' &&
      _.get(this.configurations, 'showGroups', true) !== false;
  }

  isSelected(item: any): boolean {
    if (this.configurations.multiSelect) {
        return (Array.isArray(this.selected)) ? this.selected.some(selectedItem => selectedItem.id === item.id) : false;
    } else {
        return this.selected && this.selected.id === item.id;
    }
  }

  // Add isEventFrom method to fix the error
  isEventFrom(event: Event, source: string | Element): boolean {
    if (!event || !source) return false;
    
    // If source is a string selector
    if (typeof source === 'string') {
      const element = document.querySelector(source);
      return element ? element.contains(event.target as Node) : false;
    }
    
    // If source is an Element
    return source.contains(event.target as Node);
  }

}
